import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen, faCheck, faTimes, faGripVertical, faPlus } from "@fortawesome/free-solid-svg-icons";
import CurrencyComponent from "../CurrencyComponent";
import {
  normalizeLayoutSegments,
  patchItemWithLayoutSegments,
  addTextSegmentToItem,
  SEGMENT_COMPOSITION,
} from "helper/briqueLayoutSegments";
import { groupSegmentsDroppableId } from "helper/briqueDragDropIds";

/**
 * Composant d'affichage/édition d'un item de type "Groupe de produit" dans une brique devis.
 *
 * Affiche :
 * - Le nom du groupe (éditable)
 * - Le prix unitaire (éditable)
 * - La quantité (éditable)
 * - La liste des produits composant le groupe
 * - Un texte libre optionnel
 */
const BriqueItemGroup = ({
  item,
  briqueDevisId,
  productGroups = [],
  storedCompositions = [],
  onUpdate,
  onRemove,
  dragHandleProps = {},
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue]         = useState(
    item.name_override || item.renammed || item.effective_name || ""
  );

  useEffect(() => {
    setNameValue(item.name_override || item.renammed || item.effective_name || "");
  }, [item.name_override, item.renammed, item.effective_name]);

  // Retrouve le groupe dans la liste
  const linkedGroup = productGroups.find((g) => g.id === item.product_group_id) || null;

  /*
   * 10/2026 - La composition **enregistrée sur le devis**, et non celle du catalogue du jour.
   *
   * Le document du client, le devis de production et les fiches lisent tous les enfants stockés
   * sur la ligne ; l'éditeur était le seul à afficher la carte en direct. Un produit renommé
   * depuis la vente y apparaissait sous son nouveau nom alors que le devis portait l'ancien, et
   * rien ne le signalait.
   *
   * Repli sur le catalogue pour une ligne qu'on vient d'ajouter : elle n'a pas encore d'enfants.
   */
  const stored = storedCompositions.find((c) => c.group_id === item.product_group_id) || null;
  const catalogueItems = linkedGroup?.products || [];
  const groupItems = stored && stored.products.length ? stored.products : catalogueItems;
  const vientDuDevis = Boolean(stored && stored.products.length);

  const layoutSegments = useMemo(() => normalizeLayoutSegments(item), [
    item.layout_segments,
    item.text,
    item._localId,
  ]);
  const layoutDndType = `LAY_GROUP_${item._localId}`;
  const segmentsDroppableId = groupSegmentsDroppableId(briqueDevisId, item._localId);

  const updateSegmentText = useCallback(
    (segId, text) => {
      const next = layoutSegments.map((s) =>
        s._localId === segId ? { ...s, text } : s
      );
      onUpdate(patchItemWithLayoutSegments(item, next));
    },
    [item, layoutSegments, onUpdate]
  );

  const removeTextSegment = useCallback(
    (segId) => {
      const next = layoutSegments.filter(
        (s) => s.type === SEGMENT_COMPOSITION || s._localId !== segId
      );
      onUpdate(patchItemWithLayoutSegments(item, next));
    },
    [item, layoutSegments, onUpdate]
  );

  const handleAddTextSegment = useCallback(() => {
    onUpdate(addTextSegmentToItem(item, layoutSegments.length));
  }, [item, layoutSegments.length, onUpdate]);

  const handleNameSave = () => {
    onUpdate({ ...item, name_override: nameValue });
    setIsEditingName(false);
  };

  const handleFieldChange = (field, value) => {
    onUpdate({ ...item, [field]: value === "" ? null : Number(value) });
  };

  return (
    <div className="brique-item brique-item--group mb-3 border rounded p-0 bg-white">
      {/* Header ligne groupe */}
      <Row className="align-items-center mx-0 border-bottom py-2 px-3 bg-light">
        {/* Handle de drag */}
        <Col xs="auto" className="pl-0 pr-2 text-muted" {...dragHandleProps} style={{ cursor: "grab" }}>
          <FontAwesomeIcon icon={faGripVertical} />
        </Col>

        {/* Nom du groupe (éditable) */}
        <Col md={4}>
          {isEditingName ? (
            <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
              <Form.Control
                size="sm"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                autoFocus
              />
              <Button size="sm" variant="success" onClick={handleNameSave}>
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setIsEditingName(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span
              onClick={() => setIsEditingName(true)}
              style={{ cursor: "pointer", fontWeight: 600 }}
              title="Cliquer pour modifier le nom"
            >
              {nameValue || item.effective_name || "Groupe sans nom"}
              <FontAwesomeIcon icon={faPen} className="ml-2 text-muted" style={{ fontSize: "0.75rem" }} />
            </span>
          )}
        </Col>

        {/* Prix unitaire */}
        <Col xs="3">
          <Form.Group className="mb-0">
            <Form.Label className="mb-0" style={{ fontSize: "0.75rem" }}>Prix unitaire :</Form.Label>
            <CurrencyComponent
              initialPrice={item.unit_price}
              onPriceChange={(val) => handleFieldChange("unit_price", val)}
              nullable
            />
          </Form.Group>
        </Col>

        {/* Quantité */}
        <Col xs="2">
          <Form.Group className="mb-0">
            <Form.Label className="mb-0" style={{ fontSize: "0.75rem" }}>Quantité</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              value={item.quantity ?? ""}
              onChange={(e) => handleFieldChange("quantity", e.target.value)}
              placeholder="Qté"
              min="0.01"
              step="0.01"
              style={{ width: "90px" }}
            />
          </Form.Group>
        </Col>

        {/* Total calculé */}
        {item.unit_price != null && item.quantity != null && (
          <Col xs="auto">
            <b style={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
              soit {((item.unit_price * item.quantity) / 100).toFixed(2)} €
            </b>
          </Col>
        )}

        {/* Bouton supprimer */}
        <Col xs="1">
          <Button size="sm" variant="outline-danger" onClick={onRemove} title="Supprimer cet item">
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Col>
      </Row>

      <div className="px-3 py-2">
        <Droppable droppableId={segmentsDroppableId} type={layoutDndType}>
          {(dropProvided) => (
            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
              {layoutSegments.map((seg, segIndex) => (
                <Draggable
                  key={seg._localId}
                  draggableId={`group-seg-${item._localId}-${seg._localId}`}
                  index={segIndex}
                  type={layoutDndType}
                >
                  {(dragProvided) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className="mb-2"
                    >
                      {seg.type === SEGMENT_COMPOSITION ? (
                        <div className="border rounded p-2 bg-white">
                          <div
                            className="d-flex align-items-center text-muted mb-2"
                            {...dragProvided.dragHandleProps}
                            style={{ cursor: "grab", fontSize: "0.75rem", fontWeight: 600 }}
                          >
                            <FontAwesomeIcon icon={faGripVertical} className="mr-2" />
                            {vientDuDevis ? "Contenu du groupe (ce devis)" : "Contenu du groupe (catalogue)"}
                            <span
                              className="ml-2"
                              style={{ cursor: "help", fontWeight: 400 }}
                              title={
                                vientDuDevis
                                  ? "Ce contenu est celui enregistré sur ce devis, pas celui de la carte d'aujourd'hui. C'est lui qui part sur le document du client et sur la fiche de production. Pour le remettre à jour avec la carte actuelle, retirez le groupe et rajoutez-le."
                                  : "Ce groupe n'a pas encore de contenu enregistré sur le devis : c'est la carte d'aujourd'hui qui s'affiche. Il sera figé à l'enregistrement."
                              }
                            >
                              (?)
                            </span>
                          </div>
                          {groupItems.length > 0 ? (
                            groupItems.map((gi, i) => {
                              const qty = gi.quantity || 1;
                              const name = gi.name || "Produit";
                              return (
                                <div key={i} className="small text-muted" style={{ lineHeight: 1.6 }}>
                                  <span
                                    style={{ fontWeight: 600, minWidth: "2rem", display: "inline-block" }}
                                  >
                                    {qty}x
                                  </span>
                                  {name}
                                </div>
                              );
                            })
                          ) : (
                            <p className="small text-muted mb-0">
                              {vientDuDevis ? "Aucune ligne dans ce groupe." : "Aucune ligne dans ce groupe (catalogue)."}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="border rounded p-2 bg-light">
                          <div className="d-flex align-items-start" style={{ gap: "0.35rem" }}>
                            <span
                              className="text-muted pt-1"
                              {...dragProvided.dragHandleProps}
                              style={{ cursor: "grab", flexShrink: 0 }}
                              title="Glisser pour réordonner"
                            >
                              <FontAwesomeIcon icon={faGripVertical} />
                            </span>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              size="sm"
                              value={seg.text || ""}
                              onChange={(e) => updateSegmentText(seg._localId, e.target.value)}
                              placeholder="Texte libre — avant ou après le détail du groupe"
                              style={{ resize: "vertical" }}
                            />
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="flex-shrink-0"
                              onClick={() => removeTextSegment(seg._localId)}
                              title="Supprimer ce texte"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </div>

      <div className="px-3 pb-2 d-flex" style={{ gap: "0.5rem" }}>
        <Button size="sm" variant="outline-secondary" onClick={handleAddTextSegment}>
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          Texte
        </Button>
      </div>
    </div>
  );
};

export default BriqueItemGroup;
